import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { TextField, Typography } from '@cfa/react-components';
import { Radio, RadioGroup } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import usePlanOperators from '@/hooks/usePlanOperators';
import usePlanOwner from '@/hooks/usePlanOwner';

import StepWizardPopUp, {
  type StepData,
  type StepWizardInstance,
} from '@/components/popups/StepWizardPopUp';

import { LocationDetails } from '@/_generated/api-clients/xp';
import { AddPlanData } from '@/routes/TrainingPlans/ManagePlans/Tab/Modals/AddPlan';

interface AddPlanWithOperatorsProps {
  open: boolean;
  planName?: string;
  setOpen: (isOpen: boolean) => void;
  submitHandler: (data: AddPlanData) => void;
}

// With Corp locations, choose modal list can be mixing of Operators and Corp locations
const AddPlanWithOperators: React.FC<AddPlanWithOperatorsProps> = ({
  open,
  planName,
  setOpen,
  submitHandler,
}) => {
  const { operators } = usePlanOperators();
  const { shouldSelectOwner, isMultiOwner } = usePlanOwner();

  const initialStep = shouldSelectOwner ? 1 : 2;

  const [operatorId, setOperatorId] = useState<string>();
  const [locationNumber, setLocationNumber] = useState<string>();
  const [operatorSelected, setOperatorSelected] = useState(false);
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(initialStep);
  const [stepWizard, setStepWizard] = useState<StepWizardInstance | null>(null);
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { data } = useAuth();
  const corpLocations = useMemo(
    (): LocationDetails[] => data?.leaderCorpLocations ?? [],
    [data?.leaderCorpLocations],
  );

  const operatorsNameArray = useMemo(() => {
    const nameArray: string[] = [];
    operators.forEach(op => {
      if (op.name) {
        nameArray.push(op.name);
      }
    });
    corpLocations.forEach(({ name }) => {
      if (name) {
        nameArray.push(name);
      }
    });
    return nameArray;
  }, [operators, corpLocations]);

  useEffect(() => {
    if (isMultiOwner) {
      return;
    }
    if (operators.length === 1) {
      setOperatorId(operators[0]?.id);
    } else if (corpLocations.length === 1) {
      setLocationNumber(corpLocations[0]?.number);
    }
  }, [operators, isMultiOwner, corpLocations]);

  useEffect(() => {
    if (shouldSelectOwner) {
      setOperatorId(undefined);
    }
    setStepIndex(initialStep);
    setOperatorSelected(false);
  }, [initialStep, open, shouldSelectOwner]);

  useEffect(() => {
    // we update the stepIndex anytime the initialStep is changed
    setStepIndex(initialStep);
  }, [initialStep]);

  useEffect(() => {
    if (!open || stepIndex !== 2) return;
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 150);
    return () => window.clearTimeout(timer);
  }, [open, stepIndex]);

  const closePopup = () => {
    if (shouldSelectOwner) {
      setOperatorId(undefined);
    }
    setStepIndex(shouldSelectOwner ? 1 : 2);
    setOperatorSelected(false);
    setInput(planName ?? '');
    setOpen(false);
  };

  const selectOperator = (operatorName: string) => {
    const selectedOperator = operators.find(op => op.name === operatorName);
    const selectedCorpLocation = corpLocations.find(
      ({ name }) => name === operatorName,
    );
    setOperatorId(selectedOperator?.id);
    setLocationNumber(selectedCorpLocation?.number);
    setOperatorSelected(true);
  };

  const Step1 = () => (
    <>
      <Typography variant="h3" className="mb-4">
        {t('TrainingPlans.addPlanMenu.chooseOperator')}
      </Typography>
      <div className="flex justify-center">
        <RadioGroup
          defaultValue=""
          name="operatorList"
          orientation="vertical"
          onChange={selectOperator}
        >
          {operatorsNameArray.map(operatorName => (
            <Radio value={operatorName} key={operatorName}>
              {operatorName}
            </Radio>
          ))}
        </RadioGroup>
      </div>
    </>
  );

  const Step2 = () => (
    <>
      <Typography variant="h3" className="mb-4">
        {t('Generic.giveName', { object: t('Generic.plan') })}
      </Typography>
      <TextField
        ref={inputRef}
        fullWidth
        name={'input'}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e =>
          e.key === 'Enter' &&
          submitHandler({
            ...(operatorId ? { operatorId } : { locationNumber }),
            name: input,
          })
        }
        placeholder={t('TrainingPlans.planNamePlaceholder')}
        value={input}
      />
    </>
  );

  const stepsData: Record<number, StepData> = {
    1: {
      labelButtonDeny: t('Button.cancel'),
      labelButtonNext: t('Button.next'),
      onClickBack: () => {
        closePopup();
      },
      onClickNext: () => {
        stepWizard?.nextStep();
      },
      isDisabledButtonNext: !operatorSelected,
      ContentData: Step1,
    },
    2: {
      labelButtonDeny: shouldSelectOwner
        ? t('Button.back')
        : t('Button.cancel'),
      labelButtonNext: t('Button.create'),
      onClickBack: () => {
        setInput(planName ?? '');
        if (shouldSelectOwner && stepWizard) {
          stepWizard.firstStep();
        } else {
          closePopup();
        }
      },
      onClickNext: () => {
        submitHandler({
          ...(operatorId ? { operatorId } : { locationNumber }),
          name: input,
        });
      },
      isDisabledButtonNext: !input,
      ContentData: Step2,
    },
  };

  const step = stepsData[stepIndex] ?? stepsData[initialStep];

  if (!step) {
    throw new Error(`Invalid step index: ${stepIndex}`);
  }

  return (
    <StepWizardPopUp
      closePopup={closePopup}
      initialStep={initialStep}
      open={open}
      setStepIndex={setStepIndex}
      setStepWizard={setStepWizard}
      step={step}
      steps={stepsData}
    />
  );
};

export default AddPlanWithOperators;
